import { Color, Text } from '@billy/design-system'
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Progress,
  ProgressSize,
  ProgressVariant,
  YStack,
} from '@shinetools/lumen-react'
import { ReactElement, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useSegment } from '../../../../../../hooks'
import { PromoCallout } from './elements/PromoCallout'
import { useTrialStatusBanner } from './hooks/useTrialStatusBanner'
import * as Styled from './styles'

export const TrialStatusBanner = (): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const hasTrackedEntryPointViewRef = useRef(false)
  const {
    canManageSubscription,
    headerLabel,
    isBackToSchoolCampaignActive,
    isFreePlanAudience,
    navigateToSubscriptionPlanSelection,
    progressLabel,
    progressValue,
    shouldBeVisible,
    trackCampaignEntryPointClicked,
    trackCampaignEntryPointViewed,
  } = useTrialStatusBanner()

  const handleChooseYourPlanClick = useCallback(() => {
    track('XXX - AG - Trial Widget - Compare Plans CTA clicked')

    if (isBackToSchoolCampaignActive) {
      trackCampaignEntryPointClicked({
        ctaLabel: t('navigation.menu.footer.trial_status_banner.discover_the_offer'),
        entryPoint: 'nav_offer_widget',
      })
    }

    navigateToSubscriptionPlanSelection()
  }, [isBackToSchoolCampaignActive, navigateToSubscriptionPlanSelection, t, track, trackCampaignEntryPointClicked])

  useEffect(() => {
    if (!shouldBeVisible || !isBackToSchoolCampaignActive || hasTrackedEntryPointViewRef.current) {
      return
    }

    hasTrackedEntryPointViewRef.current = true
    trackCampaignEntryPointViewed({ entryPoint: 'nav_offer_widget' })
  }, [isBackToSchoolCampaignActive, shouldBeVisible, trackCampaignEntryPointViewed])

  if (!shouldBeVisible) {
    return null
  }

  return (
    <Styled.TrialStatusBannerWrapper>
      <YStack gap={16} padding={16}>
        <YStack gap={8}>
          <Text color={Color.Lavender600} uppercase variant="bodySM" weight="semiBold">
            {headerLabel}
          </Text>
          <Progress
            label={progressLabel}
            size={ProgressSize.small}
            value={progressValue}
            variant={ProgressVariant.accent}
          />
        </YStack>
        {isBackToSchoolCampaignActive && <PromoCallout isFreePlanAudience={isFreePlanAudience} />}
        {canManageSubscription && (
          <Button
            label={
              isBackToSchoolCampaignActive
                ? t('navigation.menu.footer.trial_status_banner.discover_the_offer')
                : t('navigation.menu.footer.trial_status_banner.choose_your_plan_cta')
            }
            onClick={handleChooseYourPlanClick}
            size={ButtonSize.small}
            variant={ButtonVariant.outlined}
          />
        )}
      </YStack>
    </Styled.TrialStatusBannerWrapper>
  )
}
